import { PenLine } from 'lucide-react';
import { useStudentMarks } from '@core/firebase/marks';
import { groupMistakes, describeMistake } from '@core/quran/mistakes';
import { bundledQuran } from '@core/quran/source';
import { surahMeta } from '@core/quran/surahMeta';
import { useLanguage } from '@/contexts/LanguageContext';
import { CardTitle, SoftCard } from '@/components/PageKit';

/** Enough to show a pattern; the rest are one-offs a guardian need not scroll. */
const SHOWN = 12;

/**
 * Where the teacher corrected this pupil — the word, its place, and how many
 * times it came back. Twin of the mobile MistakeList (844de75).
 *
 * App Review's fifth refusal (2026-10-01) could not find "تصحيح التلاوة":
 * every correction was written to `marks` in a live session and never shown
 * again. This is that feature's lasting half, on the web.
 *
 * Redesign 2026-10 (board f2): a soft card, the word in the Qur'an face, and
 * a count that turns red once the same word comes back.
 */
export function MistakeList({ studentId }: { studentId: string | null }) {
  const { t, language } = useLanguage();
  const { marks, loading } = useStudentMarks(studentId);
  const mistakes = groupMistakes(marks).slice(0, SHOWN);

  const surahName = (n: number) => {
    const meta = surahMeta(n);
    if (!meta) return String(n);
    return language === 'ar' ? meta.name : meta.englishName;
  };

  return (
    <SoftCard testId="mistakes-card" className="space-y-4">
      <CardTitle icon={<PenLine />}>{t('children.mistakesTitle')}</CardTitle>

      {loading ? (
        <div className="h-16 animate-pulse rounded-[14px] bg-surface" />
      ) : mistakes.length === 0 ? (
        <p data-testid="no-mistakes" className="text-[15px] text-helper">{t('children.noMistakes')}</p>
      ) : (
        <>
          <p className="text-sm text-helper">{t('children.mistakesWhy')}</p>
          <ul className="divide-y divide-hair">
            {mistakes.map((m) => {
              const { word } = describeMistake(m, bundledQuran.words);
              const id = `mistake-${m.surah}-${m.ayah}-${m.word}`;
              return (
                <li key={id} data-testid={id} className="flex items-center justify-between gap-3 py-3">
                  <div className="min-w-0">
                    {word && <p className="ayah text-2xl leading-snug text-ink">{word}</p>}
                    <p className="text-sm text-helper">
                      {t('children.mistakePlace', { surah: surahName(m.surah), ayah: String(m.ayah) })}
                    </p>
                  </div>
                  <span
                    className={m.count > 1
                      ? 'shrink-0 rounded-full bg-danger/10 px-3 py-1 text-sm font-medium text-danger'
                      : 'shrink-0 rounded-full bg-surface px-3 py-1 text-sm text-ink'}
                  >
                    {t('children.mistakeTimes', { count: m.count, n: String(m.count) })}
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </SoftCard>
  );
}
